import React, { useState, useEffect, useRef } from 'react';
import { getMenus, getCategoriesByMenu, getSettings } from '../services/api';
import { useTranslation } from '../contexts/LanguageContext';
import './DishForm.css';

const DishForm = ({ dish, categories, menus, onSubmit, onClose, availableCategories = [] }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    priceLBP: '',
    image: null,
    categoryId: '',
    description: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [dollarRate, setDollarRate] = useState(89000);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (dish) {
      // Calculate LBP price from USD price
      const usdPrice = parseFloat(dish.price) || 0;
      const lbpPrice = usdPrice > 0 ? Math.round(usdPrice * dollarRate) : '';
      
      setFormData({
        name: dish.name || '',
        price: dish.price || '',
        priceLBP: lbpPrice,
        image: dish.imageUrl || null,
        categoryId: dish.categoryId || '',
        description: dish.description || ''
      });
      if (dish.imageUrl) {
        setImagePreview(dish.imageUrl);
      }
    } else {
      // Reset form for adding new dish
      setFormData({
        name: '',
        price: '',
        priceLBP: '',
        image: null,
        categoryId: '',
        description: ''
      });
      setImagePreview(null);
    }
  }, [dish, dollarRate]);

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      if (response.data && response.data.dollarRate) {
        setDollarRate(parseFloat(response.data.dollarRate));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleImageUpload = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target.result;
        setImagePreview(result);
        setFormData(prev => ({ ...prev, image: file })); // Store the actual file object
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleImageUpload(files[0]);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'priceLBP') {
      // Calculate USD price from LBP
      const lbpValue = parseFloat(value) || 0;
      const usdPrice = lbpValue > 0 ? (lbpValue / dollarRate).toFixed(2) : '';
      
      setFormData(prev => ({
        ...prev,
        priceLBP: value,
        price: usdPrice
      }));
    } else if (name === 'price') {
      // Calculate LBP price from USD
      const usdValue = parseFloat(value) || 0;
      const lbpPrice = usdValue > 0 ? Math.round(usdValue * dollarRate) : '';
      
      setFormData(prev => ({
        ...prev,
        price: value,
        priceLBP: lbpPrice
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData, !!dish); // Pass isEditing flag based on whether dish exists
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">
            {dish ? `✏️ ${t('edit')} ${t('dish')}` : `➕ ${t('addNewDish')}`}
          </h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('dishName')}</label>
            <input
              type="text"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('enterDishName')}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('dishPriceLBP') || 'Price in Lebanese Lira (LBP)'}</label>
            <input
              type="number"
              name="priceLBP"
              className="form-input"
              value={formData.priceLBP}
              onChange={handleChange}
              placeholder="Enter price in LBP"
              step="1000"
              min="0"
              required
            />
            <small style={{ color: '#b0b0b0', marginTop: '5px', display: 'block' }}>
              Rate: 1 USD = {dollarRate.toLocaleString()} LBP
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">{t('dishPriceUSD') || 'Price in USD (Auto-calculated)'}</label>
            <input
              type="number"
              name="price"
              className="form-input"
              value={formData.price}
              onChange={handleChange}
              placeholder="USD price will be calculated automatically"
              step="0.01"
              min="0"
              required
              readOnly
              style={{ backgroundColor: '#f5f5f5' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('dishDescription') || 'Description'}</label>
            <textarea
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('enterDishDescription') || 'Enter dish description...'}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('dishImage')}</label>
            <div 
              className={`image-upload-area ${dragActive ? 'drag-active' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="Dish preview" />
                  <button type="button" className="remove-image" onClick={removeImage}>
                    ×
                  </button>
                </div>
              ) : (
                <div className="upload-prompt">
                  <div className="upload-icon">📷</div>
                  <p>{t('dragDropImage')}</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="file-input"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('selectCategory')}</label>
            <select
              name="categoryId"
              className="form-select"
              value={formData.categoryId}
              onChange={handleChange}
              required
            >
              <option value="">{t('choose')}</option>
              {availableCategories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            {availableCategories.length === 0 && (
              <small style={{ color: '#b0b0b0' }}>{t('noCategoriesAvailable')}</small>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('cancel')}
            </button>
            <button type="submit" className="btn btn-primary">
              {dish ? `${t('update')} ${t('dish')}` : `${t('add')} ${t('dish')}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DishForm;
